/**
 * Walks the current DOM and produces a compact textual outline for the
 * outfitter assistant to send to the backend LLM as visual context.
 *
 * Output format is compatible with Playwright's `ariaSnapshot({ forAI: true })`
 * / `toMatchAriaSnapshot`:
 *
 *     - navigation "Main":
 *       - link "Home"
 *       - link "Settings" [current=page]
 *     - main:
 *       - heading "Bookings" [level=1]
 *       - table:
 *         - columns: Name | Status
 *         - row: Guest 0 | Confirmed
 *
 * References:
 * - Playwright aria snapshot reference:
 *     https://playwright.dev/docs/aria-snapshots
 * - Why the aria tree is the right shape for AI agents (Microsoft Playwright
 *   MCP, browserclaw, Mastra AgentBrowser all converge on this format):
 *     https://github.com/microsoft/playwright-mcp
 *     https://www.npmjs.com/package/browserclaw
 *     https://mastra.ai/docs/browser/agent-browser/llms.txt
 *
 * Kept custom because every popular library that produces this shape
 * (Playwright, browserclaw, @mastra/agent-browser, textweb, ...) runs
 * Playwright/CDP *outside* the browser tab. None of them can serialize the
 * live DOM that the user is looking at, which is exactly what we need for an
 * in-app assistant. The two spec-heavy primitives (accessible name + role
 * resolution) are delegated to `dom-accessibility-api` for reliability — see
 * `assistant-page-snapshot-describe.ts`. The traversal itself lives in
 * `assistant-page-snapshot-walker.ts` so this file stays focused on the
 * public entry point.
 */
import { captureException } from "@sentry/react"

import {
    OUTFITTER_ASSISTANT_VISUAL_CONTEXT_MODE_NONE,
    OUTFITTER_ASSISTANT_VISUAL_CONTEXT_MODE_UI_SUMMARY,
} from "~components/outfitter-assistant/outfitter-assistant-constants"
import { OutlineWalker } from "~utils/helpers/assistant-page-snapshot-walker"

/**
 * Builds a compact, aria-oriented outline of the current page for the
 * assistant. Runs synchronously; never throws — callers get `{ mode: "none" }`
 * when the DOM isn't usable (SSR, walker failure, etc.).
 */
export function captureAssistantPageSnapshot(
    root: Element | null = defaultRoot()
): AssistantPageSnapshot {
    if (!root) {
        return { mode: OUTFITTER_ASSISTANT_VISUAL_CONTEXT_MODE_NONE }
    }

    try {
        const walker = new OutlineWalker()
        walker.walk(root, 0)
        const summary = walker.toString()

        if (!summary) {
            return { mode: OUTFITTER_ASSISTANT_VISUAL_CONTEXT_MODE_NONE }
        }

        return {
            mode: OUTFITTER_ASSISTANT_VISUAL_CONTEXT_MODE_UI_SUMMARY,
            summary,
            truncated: walker.truncated,
        }
    } catch (error) {
        // Surface walker failures to Sentry so we can learn about DOM edge
        // cases that break the outline, then silently degrade so the
        // assistant send path keeps working.
        captureException(error, {
            tags: { feature: "outfitter-assistant-snapshot" },
        })
        return { mode: OUTFITTER_ASSISTANT_VISUAL_CONTEXT_MODE_NONE }
    }
}

function defaultRoot(): Element | null {
    if (typeof document === "undefined") return null
    return document.body
}

interface AssistantPageSnapshot {
    mode:
        | typeof OUTFITTER_ASSISTANT_VISUAL_CONTEXT_MODE_NONE
        | typeof OUTFITTER_ASSISTANT_VISUAL_CONTEXT_MODE_UI_SUMMARY
    summary?: string
    truncated?: boolean
}
